import { Navigate, NavLink, Outlet, useNavigate, Route, Routes } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Shield, Heart, LayoutDashboard, BookOpen, ShoppingBag, MessageSquare, FileText, LogOut, Users, Inbox, Sparkles, MessagesSquare } from "lucide-react";
import AdminOverview from "@/pages/admin/AdminOverview";
import AdminCourses from "@/pages/admin/AdminCourses";
import AdminProducts from "@/pages/admin/AdminProducts";
import AdminCommunity from "@/pages/admin/AdminCommunity";
import AdminBlog from "@/pages/admin/AdminBlog";
import AdminUsers from "@/pages/admin/AdminUsers";
import AdminBookings from "@/pages/admin/AdminBookings";
import AdminCurriculum from "@/pages/admin/AdminCurriculum";
import AdminHomeContent from "@/pages/admin/AdminHomeContent";

export default function AdminLayout() {
  const { user, ready, logout } = useAuth();
  const nav = useNavigate();

  if (!ready) return <div className="min-h-screen bg-neutral-950 text-white/50 flex items-center justify-center">Loading…</div>;
  if (!user) return <Navigate to="/admin/login" replace />;
  if (user.role !== "admin") return <Navigate to="/" replace />;

  const links = [
    { to: "/admin", label: "Overview", icon: LayoutDashboard, end: true },
    { to: "/admin/curriculum", label: "Curriculum", icon: Sparkles },
    { to: "/admin/courses", label: "Courses", icon: BookOpen },
    { to: "/admin/products", label: "Products", icon: ShoppingBag },
    { to: "/admin/community", label: "Community", icon: MessageSquare },
    { to: "/admin/blog", label: "Blog", icon: FileText },
    { to: "/admin/home-content", label: "Home Content", icon: MessagesSquare },
    { to: "/admin/bookings", label: "Bookings", icon: Inbox },
    { to: "/admin/users", label: "Users", icon: Users },
  ];

  return (
    <div className="min-h-screen bg-neutral-950 text-white">
      <aside className="fixed left-0 top-0 h-full w-64 border-r border-white/10 bg-black/70 backdrop-blur-xl px-6 py-8 flex flex-col">
        <div className="flex items-center gap-2 mb-10">
          <div className="w-9 h-9 rounded-full bg-brand-pink/20 flex items-center justify-center">
            <Shield className="w-4 h-4 text-brand-pink" />
          </div>
          <div className="leading-tight">
            <div className="font-display text-lg">Host <span className="font-script text-brand-pink">Hustle</span></div>
            <div className="text-[9px] tracking-[0.3em] uppercase text-white/50">Admin Console</div>
          </div>
        </div>
        <nav className="space-y-1 flex-1">
          {links.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              end={l.end}
              data-testid={`admin-nav-${l.label.toLowerCase()}`}
              className={({ isActive }) => `flex items-center gap-3 px-4 py-2.5 rounded-full text-sm transition ${isActive ? "bg-brand-pink text-white" : "text-white/70 hover:text-white hover:bg-white/5"}`}
            >
              <l.icon className="w-4 h-4" /> {l.label}
            </NavLink>
          ))}
        </nav>
        <button
          data-testid="admin-logout"
          onClick={() => { logout(); nav("/admin/login"); }}
          className="mt-6 inline-flex items-center gap-2 text-sm text-white/60 hover:text-white"
        >
          <LogOut className="w-4 h-4" /> Sign out
        </button>
        <div className="mt-6 text-[10px] text-white/30 tracking-widest uppercase inline-flex items-center gap-1">
          <Heart className="w-3 h-3 text-brand-pink" fill="#E91E63" /> {user.email}
        </div>
      </aside>

      <main className="pl-64 min-h-screen">
        <div className="max-w-6xl mx-auto p-10">
          <Routes>
            <Route index element={<AdminOverview />} />
            <Route path="curriculum" element={<AdminCurriculum />} />
            <Route path="courses" element={<AdminCourses />} />
            <Route path="products" element={<AdminProducts />} />
            <Route path="community" element={<AdminCommunity />} />
            <Route path="blog" element={<AdminBlog />} />
            <Route path="home-content" element={<AdminHomeContent />} />
            <Route path="bookings" element={<AdminBookings />} />
            <Route path="users" element={<AdminUsers />} />
          </Routes>
        </div>
      </main>
    </div>
  );
}
